/**
 * Centralized Axios instance with organizationId interceptor
 * Automatically adds organizationId to all API requests when available
 * This enables multi-tenancy for subscription-based hospitals
 */

import axios from "axios";
import { getOrganizationId } from "../utils/organizationUtils";
import { BACKEND_URL } from "../utils/envConfig";
import {
  trackHttpRequestEnd,
  trackHttpRequestStart,
} from "../utils/httpInflight";

// Create axios instance
const apiClient = axios.create({
  baseURL: BACKEND_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Flag to prevent multiple concurrent refreshes
let isRefreshingOrgId = false;

// Request interceptor to automatically add organizationId
apiClient.interceptors.request.use(
  (config) => {
    trackHttpRequestStart();
    // Get organizationId from localStorage (user object)
    // Only add organizationId if user belongs to a hospital organization
    // Common users (without organizationId) should use base tables
    // IMPORTANT: Always read from localStorage.user (not localStorage.organizationId) to get latest value
    const storedUser = localStorage.getItem("user");
    let organizationId = null;
    
    // Skip refresh for login/register endpoints to avoid infinite loops
    // Note: /staff/user/ and /doctors/user/ are NOT excluded - they need organizationId if available
    // But if organizationId is null, they won't get it added, allowing backend to search across tables
    const isAuthEndpoint = config.url && (
      config.url.includes('/login') || 
      config.url.includes('/register')
    );
    
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        // First try to get organizationId from user object (source of truth)
        let orgId = user?.organizationId;
        
        // Check if this is a base user (organizationId is explicitly null by design)
        // Base users should NOT trigger organizationId refresh
        // Base user: organizationId is explicitly null (not undefined, not missing)
        // BUT: super_admin is NOT a base user - they can have organizationId set temporarily
        const normalizedRole = (user?.roleName || "").toLowerCase();
        const isSuperAdmin = normalizedRole === 'superadmin' || normalizedRole === 'super_admin';
        const isBaseUser = !isSuperAdmin && user.organizationId === null;
        
        // If not in user object, check localStorage.organizationId as fallback
        // This handles cases where organizationId was set during login but user object wasn't updated
        // This is especially important for super_admin who gets organizationId set during login
        // BUT: Skip fallback for base users (they intentionally don't have organizationId)
        if (!isBaseUser && (orgId == null || orgId === "" || isNaN(orgId) || Number(orgId) <= 0)) {
          const fallbackOrgId = localStorage.getItem("organizationId");
          if (fallbackOrgId != null && fallbackOrgId !== "" && !isNaN(fallbackOrgId) && Number(fallbackOrgId) > 0) {
            orgId = fallbackOrgId;
            // Update user object with organizationId to keep it in sync
            const updatedUser = { ...user, organizationId: Number(orgId) };
            localStorage.setItem("user", JSON.stringify(updatedUser));
            // Dispatch event to update AuthContext
            window.dispatchEvent(new CustomEvent('userOrganizationIdUpdated', { 
              detail: { updatedUser } 
            }));
          }
        }
        
        // If still no organizationId and this is not an auth endpoint, trigger refresh in background
        // BUT: Skip refresh for base users (they intentionally don't have organizationId)
        if (!isBaseUser && (orgId == null || orgId === "" || isNaN(orgId) || Number(orgId) <= 0) && !isAuthEndpoint && !isRefreshingOrgId) {
          isRefreshingOrgId = true;
          console.log(`[apiClient] ⚠️ No organizationId found for user ${user?.id}, triggering background refresh...`);
          // Trigger refresh in background (non-blocking)
          import("../utils/organizationRefresh").then(({ refreshOrganizationId }) => {
            refreshOrganizationId().then((refreshedOrgId) => {
              if (refreshedOrgId) {
                console.log(`[apiClient] ✅ Background refresh found organizationId: ${refreshedOrgId}`);
                // Update user object
                const updatedUser = { ...user, organizationId: refreshedOrgId };
                localStorage.setItem("user", JSON.stringify(updatedUser));
                localStorage.setItem("organizationId", refreshedOrgId.toString());
                // Reload page to apply organizationId to all subsequent requests
                console.log(`[apiClient] Reloading page to apply organizationId...`);
                setTimeout(() => window.location.reload(), 1000);
              } else {
                console.warn(`[apiClient] ⚠️ Background refresh did not find organizationId`);
              }
            }).catch((refreshError) => {
              console.error("[apiClient] Error during background refresh:", refreshError);
            }).finally(() => {
              isRefreshingOrgId = false;
            });
          }).catch((importError) => {
            console.error("[apiClient] Error importing refreshOrganizationId:", importError);
            isRefreshingOrgId = false;
          });
        } else if (isBaseUser) {
          // Base user - explicitly set organizationId to null and don't add it to requests
          console.log(`[apiClient] Base user detected (organizationId is null) - will use base tables`);
          organizationId = null;
        }
        
        if (orgId != null && orgId !== "" && !isNaN(orgId) && Number(orgId) > 0) {
          organizationId = Number(orgId);
        }
      } catch (e) {
        console.error("[apiClient] Error parsing stored user:", e);
      }
    }
    
    // Log organizationId for all requests to help debug base user issues
    const isBaseUser = storedUser ? (() => {
      try {
        const user = JSON.parse(storedUser);
        // super_admin is NOT a base user - they can have organizationId set temporarily
        const normalizedRole = (user?.roleName || "").toLowerCase();
        const isSuperAdmin = normalizedRole === 'superadmin' || normalizedRole === 'super_admin';
        return !isSuperAdmin && user.organizationId === null;
      } catch (e) {
        return false;
      }
    })() : false;
    
    if (isBaseUser) {
      console.log(`[apiClient] 🔵 Base user request to ${config.url} - organizationId: null (will NOT be added to request)`);
    } else if (config.url && (config.url.includes('/dashboard') || config.url.includes('/appointments') || config.url.includes('/patients'))) {
      console.log(`[apiClient] Request to ${config.url} - organizationId: ${organizationId || 'none'}`);
    }
    
    // Only add organizationId if it's a valid positive number
    // This ensures common users (without organizationId) don't send it,
    // allowing backend to use base tables
    if (organizationId && organizationId > 0) {
      const method = (config.method || "").toLowerCase();
      
      // Check if user is super_admin
      const storedUser = localStorage.getItem("user");
      let isSuperAdmin = false;
      if (storedUser) {
        try {
          const user = JSON.parse(storedUser);
          const normalizedRole = (user?.roleName || "").toLowerCase();
          isSuperAdmin = normalizedRole === 'superadmin' || normalizedRole === 'super_admin';
          if (isSuperAdmin && config.url && (config.url.includes('/dashboard') || config.url.includes('/wards') || config.url.includes('/tokens') || config.url.includes('/doctors') || config.url.includes('/staff'))) {
            console.log(`[apiClient] Super admin request to ${config.url} - organizationId: ${organizationId}`);
          }
        } catch (e) {
          // Ignore parsing errors
        }
      }
      
      // For super_admin, skip organizationId ONLY for user-specific endpoints
      // These endpoints query user records that might not belong to the selected organization
      // User-specific endpoints: /staff/user/{userId}, /doctors/user/{userId}, etc.
      // All other endpoints (like /dashboard, /wards, /tokens, etc.) should get organizationId
      const isUserSpecificEndpoint = config.url && (
        config.url.includes('/staff/user/') ||
        config.url.includes('/doctors/user/') ||
        config.url.match(/\/staff\/user\/\d+/) ||
        config.url.match(/\/doctors\/user\/\d+/)
      );
      
      // For super_admin with user-specific endpoints, don't send organizationId
      // This allows the backend to search across all organizations
      if (isSuperAdmin && isUserSpecificEndpoint) {
        console.log(`[apiClient] Super admin - skipping organizationId for user-specific endpoint: ${config.url}`);
        // Remove organizationId from params if it was already added
        if (config.params && config.params.organizationId) {
          delete config.params.organizationId;
        }
      } else {
        // For all other cases (including super_admin with non-user-specific endpoints),
        // add organizationId based on HTTP method
        if (method === "get") {
          // Keep an explicit organizationId from the caller (e.g. patient
          // landing hospital filter). Do not replace it with the logged-in user's org.
          const explicitOrgId = Number(config.params?.organizationId);
          config.params = {
            ...config.params,
            organizationId:
              Number.isFinite(explicitOrgId) && explicitOrgId > 0
                ? explicitOrgId
                : organizationId,
          };
          if (config.url && (config.url.includes('/dashboard') || config.url.includes('/wards') || config.url.includes('/tokens'))) {
            console.log(`[apiClient] Added organizationId=${organizationId} to GET request params for ${config.url}`);
          }
        } else if (method === "delete") {
          // Add organizationId as query parameter for DELETE requests as well
          // so that deletions are scoped to the organization-specific tables
          config.params = {
            ...config.params,
            organizationId: organizationId,
          };
        } else if (method === "post" || method === "put" || method === "patch") {
          // This will be handled below in the POST/PUT/PATCH section
          // We'll add organizationId there
        }
      }
      
      // Handle POST/PUT/PATCH requests (outside the else block to maintain structure)
      if (!(isSuperAdmin && isUserSpecificEndpoint) && (method === "post" || method === "put" || method === "patch")) {
        // Add organizationId to request body for POST/PUT/PATCH requests
        // EXCEPT for endpoints that don't accept it in the body (send as query param instead)
        // Check if this endpoint should receive organizationId as query parameter instead of in body
        // Some backend DTOs don't accept organizationId in the request body
        // For PUT requests to /patients and /appointments, send as query param
        // For POST requests, allow in body (backend may need it there)
        const endpointsWithQueryParamOrgIdForPut = [
          '/medicines', // MedicineRawDetailsDTO doesn't accept organizationId in body
          '/emergency-admissions', // Emergency Admission DTO doesn't accept organizationId in body for PUT requests - backend uses it as query param to route to correct organization-specific table
        ];
        
        // Endpoints that need organizationId as query param for both POST and PUT
        const endpointsWithQueryParamOrgIdForAll = [
          '/wards', // Ward DTO doesn't accept organizationId in body
          '/beds', // Bed DTO doesn't accept organizationId in body
          '/attendance', // Attendance/Leave DTO doesn't accept organizationId in body
          '/medicine-transactions', // Medicine transaction DTO doesn't accept organizationId in body
          '/pathology-transactions', // Pathology transaction DTO doesn't accept organizationId in body
          '/sample-transactions', // Sample transaction routing uses query param → {orgId}_sample_transactions (not body / base table)
          '/pathlabreports', // Path lab reports need organizationId as query param for proper filtering
          '/patient_pathology', // Patient pathology DTO doesn't accept organizationId in body - backend uses query param to route to {orgId}_patient_pathology
          '/lab/boms', // BOM endpoints need organizationId as query param
          '/labtests', // Lab tests endpoints need organizationId as query param
          '/inventory', // Inventory endpoints need organizationId as query param for POST to ensure correct table selection and foreign key references
          '/prescriptions', // Prescriptions need organizationId as query param for POST to ensure lab tests are looked up in org-specific table
          '/ipd-prescriptions', // IPD prescriptions need organizationId as query param for POST to ensure correct table selection
          '/ipd-lab-tests', // IPD lab tests need organizationId as query param for POST (array payload would be corrupted if added to body)
          '/invoices', // Invoices need hospital organizationId as query param (for table routing) but credit organizationId in body (for storing in invoice record)
          '/appointments/create', // Appointment create must route to {orgId}_appointment via query param
          '/appointments/reminders', // Reminder DTO should get organizationId as query param for org table routing
          '/medicine-requests', // MedicineRequestDTO doesn't accept organizationId in body - backend uses it as query param to route to medicine_raw_details (base) or org-specific medicine_inventory table
          '/patient-medicine-inventory', // PatientMedicineInventoryDTO doesn't accept organizationId in body - backend uses it as query param to route to {orgId}_patient_medicine_inventory and {orgId}_medicine_administration_history tables
          '/patient-consumables', // PatientConsumableDTO doesn't accept organizationId in body - backend uses it as query param to route to {orgId}_patient_consumables table
          '/ipd-admissions', // IPD admissions status update needs organizationId as query param to avoid transaction conflicts
          '/emergency-admissions', // Emergency Admission DTO doesn't accept organizationId in body - backend uses it as query param to route to {orgId}_emergency_admission table
          '/emergency-details', // Emergency Details DTO doesn't accept organizationId in body - backend uses it as query param to route to {orgId}_emergency_details table
          '/kardex', // Kardex DTOs don't accept organizationId in body - backend uses it as query param to route to {orgId}_kardex tables
          '/kardex-diet-plans', // Diet plans kardex endpoint (different URL pattern)
          '/kardex-investigations', // Investigations kardex endpoint (different URL pattern)
          // Explicitly include sub-paths to ensure matching works correctly
          '/patient-medicine-inventory/return', // Return endpoint - organizationId should be query param only
          '/patient-medicine-inventory/add', // Add endpoint - organizationId should be query param only
          '/patient-medicine-inventory/administer', // Administer endpoint - organizationId should be query param only
        ];
        
        // For appointments, only use query param for PUT (updates), not POST (create)
        const isAppointmentPut = config.url && config.url.includes('/appointments') && 
                                 (config.method === 'put' || config.method === 'PUT');
        
        // Check if endpoint needs query param for PUT requests
        const needsQueryParamForPut = endpointsWithQueryParamOrgIdForPut.some(endpoint => 
          config.url && config.url.includes(endpoint)
        ) || isAppointmentPut;
        
        // Check if endpoint needs query param for all methods (POST and PUT)
        const needsQueryParamForAll = endpointsWithQueryParamOrgIdForAll.some(endpoint => 
          config.url && config.url.includes(endpoint)
        );
        
        const shouldUseQueryParam = needsQueryParamForPut || needsQueryParamForAll;
        
        // For super_admin, skip organizationId for user-specific endpoints even in POST/PUT/PATCH
        if (shouldUseQueryParam && !(isSuperAdmin && isUserSpecificEndpoint)) {
          // Send organizationId as query parameter for these endpoints.
          // Keep an explicit organizationId from the caller (e.g. patient
          // portal booking at a hospital other than the logged-in user's org).
          const explicitParamOrgId = Number(config.params?.organizationId);
          config.params = config.params || {};
          config.params.organizationId =
            Number.isFinite(explicitParamOrgId) && explicitParamOrgId > 0
              ? explicitParamOrgId
              : organizationId;
          
          // Remove organizationId from request body if it exists (for these endpoints)
          // EXCEPT for invoices: keep organizationId in body (it's the hospital org ID needed for patient lookup)
          // Handle arrays specially - don't try to remove organizationId from arrays
          if (config.data && typeof config.data === 'object' && !(config.data instanceof FormData) && !Array.isArray(config.data)) {
            const isInvoiceEndpoint = config.url && config.url.includes('/invoices');
            // For invoices: keep organizationId in body (hospital org ID for patient lookup)
            // Also keep creditOrganizationId if present (credit org ID for storing in invoice record)
            if (!isInvoiceEndpoint) {
              // Remove organizationId from request body for other endpoints
              // BUT: For super_admin with user-specific endpoints, we already skipped adding it to params,
              // so we should also skip removing it from body (it might be needed for the request)
              if (!(isSuperAdmin && isUserSpecificEndpoint)) {
                const { organizationId: orgIdInBody, ...restData } = config.data;
                config.data = restData;
              }
            }
            // For invoices, keep both organizationId (hospital org ID) and creditOrganizationId (credit org ID if present)
          }
          
          // For array payloads with kardex endpoints, ensure organizationId is in query params
          // (arrays can't have organizationId in body, so it must be in query params)
          // This covers all kardex endpoints: /kardex/*, /kardex-diet-plans, /kardex-investigations
          if (Array.isArray(config.data) && config.url && (
            config.url.includes('/kardex') || 
            config.url.includes('/kardex-diet-plans') || 
            config.url.includes('/kardex-investigations') ||
            config.url.includes('/kardex/nursing-care') ||
            config.url.includes('/kardex/elimination-output') ||
            config.url.includes('/kardex/medical-history') ||
            config.url.includes('/kardex/vital-signs') ||
            config.url.includes('/kardex/iv-fluids') ||
            config.url.includes('/kardex/devices-procedures') ||
            config.url.includes('/kardex/mobility') ||
            config.url.includes('/kardex/nurse-notes') ||
            config.url.includes('/kardex/patient-communication') ||
            config.url.includes('/kardex/misc-alerts') ||
            config.url.includes('/kardex/medications')
          )) {
            // Double-check that organizationId is set in params for array payloads
            config.params = config.params || {};
            config.params.organizationId = organizationId;
          }
        } else {
          // For other endpoints, add to request body
          // BUT: For super_admin with user-specific endpoints, skip adding organizationId
          if (!(isSuperAdmin && isUserSpecificEndpoint)) {
            // For FormData, we need to append it differently
            if (config.data instanceof FormData) {
              config.data.append('organizationId', organizationId);
            } else if (Array.isArray(config.data)) {
              // If data is an array, we cannot add organizationId to it without corrupting the array
              // In this case, send organizationId as query parameter instead
              console.warn(`[apiClient] Request data is an array for ${config.url}, sending organizationId as query parameter to avoid corrupting array`);
              config.params = {
                ...config.params,
                organizationId: organizationId,
              };
              // Keep the array as-is
            } else {
              config.data = {
                ...config.data,
                organizationId: organizationId,
              };
            }
          }
        }
      }
    }
    // If no organizationId, don't add it - backend will use base tables
    
    return config;
  },
  (error) => {
    trackHttpRequestEnd();
    return Promise.reject(error);
  }
);

// Response interceptor for error handling (optional)
apiClient.interceptors.response.use(
  (response) => {
    trackHttpRequestEnd();
    return response;
  },
  (error) => {
    trackHttpRequestEnd();
    // Handle errors if needed
    return Promise.reject(error);
  }
);

export default apiClient;

